import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PERMISSIONS,
  type PurchaseInvoiceSummary,
  type PurchaseOrderSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { LoadingState } from '@/components/ui/LoadingState';
import { PageHeader } from '@/components/ui/PageHeader';
import { BooleanStatusBadge } from '@/components/ui/StatusBadge';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { usePurchaseInvoicesQuery } from '../api/purchase-invoices.api';
import { usePurchaseOrdersQuery } from '../api/purchase-orders.api';
import { useSupplierActivationMutation, useSupplierQuery } from '../api/suppliers.api';
import { SupplierPayablesCard } from '@/modules/payables/components/SupplierPayablesCard';
import { PurchaseInvoiceStatusBadge } from '../components/PurchaseInvoiceStatusBadge';
import { PurchaseOrderStatusBadge } from '../components/PurchaseOrderStatusBadge';
import { SupplierFormDialog } from '../components/SupplierFormDialog';

/** A field and its value, or a dash when the supplier never gave one. */
function Detail({
  label,
  value,
  ltr = false,
}: {
  label: string;
  value: string | null;
  ltr?: boolean;
}) {
  return (
    <div className="space-y-0.5">
      <dt className="text-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm" dir={ltr ? 'ltr' : 'auto'}>
        {value?.trim() ? value : '—'}
      </dd>
    </div>
  );
}

/**
 * One supplier: who they are, and what has been ordered from them.
 *
 * A supplier is never deleted, only taken out of use, so everything already ordered from one stays
 * readable — which is why an inactive supplier still opens here and still shows its orders.
 */
export function SupplierDetailPage() {
  const { t } = useTranslation();
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { format } = useCurrency();

  const [isEditing, setIsEditing] = useState(false);
  const [confirming, setConfirming] = useState<'deactivate' | 'reactivate' | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isPending, isError, error, refetch } = useSupplierQuery(supplierId);
  const canManage = can(PERMISSIONS.SUPPLIERS_MANAGE);
  const canSeeOrders = can(PERMISSIONS.PURCHASING_VIEW);
  // Finance reads what a supplier is owed without needing to manage the supplier record itself.
  const canSeePayables = can(PERMISSIONS.PAYABLES_VIEW);

  const deactivate = useSupplierActivationMutation('deactivate');
  const reactivate = useSupplierActivationMutation('reactivate');
  const activation = confirming === 'reactivate' ? reactivate : deactivate;

  const orders = usePurchaseOrdersQuery({ supplierId: supplierId ?? '', pageSize: 5 });
  const invoices = usePurchaseInvoicesQuery(
    { supplierId: supplierId ?? '', pageSize: 5 },
    { enabled: canSeeOrders },
  );

  if (isPending) return <LoadingState />;
  if (isError || !data) {
    return (
      <div className="space-y-3">
        <Alert tone="error">{apiErrorMessage(error) ?? t('purchasing.suppliers.loadError')}</Alert>
        <Button variant="secondary" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const invoiceColumns: Column<PurchaseInvoiceSummary>[] = [
    {
      key: 'reference',
      header: t('purchasing.invoices.fields.reference'),
      primary: true,
      render: (invoice) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono text-sm" dir="ltr">
            {invoice.reference}
          </span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {invoice.supplierInvoiceNumber}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('inventory.fields.state'),
      render: (invoice) => <PurchaseInvoiceStatusBadge status={invoice.status} />,
    },
    {
      key: 'dueDate',
      header: t('purchasing.invoices.fields.dueDate'),
      render: (invoice) => (
        <span className="text-muted" dir="ltr">
          {invoice.dueDate}
        </span>
      ),
    },
    {
      key: 'obligation',
      header: t('purchasing.invoices.fields.obligation'),
      align: 'end',
      render: (invoice) => (
        <span className="text-ink" dir="ltr">
          {format(invoice.obligationMinor)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (invoice) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/invoices/${invoice.id}`)}
          aria-label={t('purchasing.invoices.openNamed', { reference: invoice.reference })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  const orderColumns: Column<PurchaseOrderSummary>[] = [
    {
      key: 'reference',
      header: t('purchasing.orders.fields.reference'),
      primary: true,
      render: (order) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {order.reference}
          </span>
          <span className="text-muted text-xs">{order.branchName}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('inventory.fields.state'),
      render: (order) => <PurchaseOrderStatusBadge status={order.status} />,
    },
    {
      key: 'date',
      header: t('purchasing.orders.fields.orderedOn'),
      render: (order) => (
        <span className="text-muted" dir="ltr">
          {order.orderedOn ?? '—'}
        </span>
      ),
    },
    {
      key: 'total',
      header: t('purchasing.orders.fields.total'),
      align: 'end',
      render: (order) => (
        <span className="text-ink" dir="ltr">
          {format(order.totalMinor)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (order) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate(`/purchasing/orders/${order.id}`)}
          aria-label={t('purchasing.orders.openNamed', { reference: order.reference })}
        >
          {t('common.view')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.name}
        subtitle={data.supplierNumber}
        actions={
          canManage ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => setIsEditing(true)}>
                {t('common.edit')}
              </Button>
              <Button
                variant={data.isActive ? 'danger' : 'secondary'}
                onClick={() => setConfirming(data.isActive ? 'deactivate' : 'reactivate')}
              >
                {data.isActive
                  ? t('purchasing.suppliers.deactivate')
                  : t('purchasing.suppliers.reactivate')}
              </Button>
            </div>
          ) : undefined
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {!data.isActive ? (
        <Alert tone="warning">{t('purchasing.suppliers.inactiveNotice')}</Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('purchasing.suppliers.details')}
          action={<BooleanStatusBadge isActive={data.isActive} />}
        />
        <CardBody>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Detail label={t('purchasing.suppliers.fields.nameAr')} value={data.nameAr} />
            <Detail
              label={t('purchasing.suppliers.fields.contactPerson')}
              value={data.contactPerson}
            />
            <Detail label={t('purchasing.suppliers.fields.phone')} value={data.phone} ltr />
            <Detail label={t('purchasing.suppliers.fields.email')} value={data.email} ltr />
            <Detail label={t('purchasing.suppliers.fields.taxNumber')} value={data.taxNumber} ltr />
            <Detail
              label={t('purchasing.suppliers.fields.paymentTermsDays')}
              value={t('purchasing.suppliers.days', { count: data.paymentTermsDays })}
            />
            <Detail label={t('purchasing.suppliers.fields.address')} value={data.address} />
            <Detail label={t('purchasing.suppliers.fields.notes')} value={data.notes} />
          </dl>
        </CardBody>
      </Card>

      {canSeePayables ? <SupplierPayablesCard supplierId={data.id} /> : null}

      {canSeeOrders ? (
        <Card>
          <CardHeader
            title={t('purchasing.suppliers.recentOrders')}
            subtitle={t('purchasing.suppliers.recentOrdersHint')}
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void navigate(`/purchasing/orders?supplierId=${data.id}`)}
              >
                {t('purchasing.suppliers.viewAllOrders')}
              </Button>
            }
          />
          <DataTable
            caption={t('purchasing.suppliers.recentOrders')}
            columns={orderColumns}
            rows={orders.data?.items}
            rowKey={(order) => order.id}
            isLoading={orders.isPending}
            isError={orders.isError}
            errorMessage={apiErrorMessage(orders.error) ?? t('purchasing.orders.loadError')}
            onRetry={() => void orders.refetch()}
            emptyTitle={t('purchasing.suppliers.noOrders')}
          />
        </Card>
      ) : null}

      {canSeeOrders ? (
        <Card>
          <CardHeader
            title={t('purchasing.invoices.title')}
            subtitle={t('purchasing.suppliers.recentInvoicesHint')}
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void navigate(`/purchasing/invoices?supplierId=${data.id}`)}
              >
                {t('purchasing.suppliers.viewAllInvoices')}
              </Button>
            }
          />
          <DataTable
            caption={t('purchasing.invoices.title')}
            columns={invoiceColumns}
            rows={invoices.data?.items}
            rowKey={(invoice) => invoice.id}
            isLoading={invoices.isPending}
            isError={invoices.isError}
            errorMessage={apiErrorMessage(invoices.error) ?? t('purchasing.invoices.loadError')}
            onRetry={() => void invoices.refetch()}
            emptyTitle={t('purchasing.suppliers.noInvoices')}
          />
        </Card>
      ) : null}

      <SupplierFormDialog
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        supplier={data}
        onSaved={() => {
          setIsEditing(false);
          setFeedback(t('purchasing.suppliers.saved'));
        }}
      />

      <ConfirmDialog
        isOpen={confirming !== null}
        onCancel={() => setConfirming(null)}
        title={
          confirming === 'reactivate'
            ? t('purchasing.suppliers.reactivate')
            : t('purchasing.suppliers.deactivate')
        }
        message={
          confirming === 'reactivate'
            ? t('purchasing.suppliers.reactivateHint')
            : t('purchasing.suppliers.deactivateHint')
        }
        isDestructive={confirming === 'deactivate'}
        isConfirming={activation.isPending}
        onConfirm={() =>
          activation.mutate(
            { id: data.id, version: data.version },
            {
              onSuccess: () => {
                setFeedback(
                  confirming === 'reactivate'
                    ? t('purchasing.suppliers.reactivated')
                    : t('purchasing.suppliers.deactivated'),
                );
                setConfirming(null);
              },
            },
          )
        }
      />

      {activation.isError ? (
        <Alert tone="error">
          {apiErrorMessage(activation.error) ?? t('purchasing.suppliers.saveError')}
        </Alert>
      ) : null}
    </div>
  );
}
